import { ColaboradorEnum, ColaboradorList } from '@/modules/people/enums/tipo-vinculo.enum';
import {
  OperationalProfileData,
  OperationalProfileEntry,
  OperationalProfileKind,
  OperationalProfileRemoval,
  PersonFormDto,
} from '@/modules/people/presentation/person-form.utils';
import { ReadPersonDto } from '@/api.types';
import { DataAmigavel } from '@/shared/utils/datas';

export type { OperationalProfileKind, OperationalProfileEntry, OperationalProfileData, OperationalProfileRemoval };

export const OPERATIONAL_PROFILE_KINDS: OperationalProfileKind[] = [
  'defensor',
  'estagiario',
  'colaborador',
];

export const OPERATIONAL_PROFILE_LABELS: Record<OperationalProfileKind, string> = {
  defensor: 'Defensor',
  estagiario: 'Estagiário',
  colaborador: 'Colaborador',
};

type ProfileLike = OperationalProfileData;

function formatDate(value?: string | Date | null): string {
  if (!value) return '—';
  return DataAmigavel(value);
}

function newLocalKey(kind: OperationalProfileKind, id?: number): string {
  return id ? `${kind}-${id}` : `${kind}-draft-${Date.now()}`;
}

function toEntry(
  kind: OperationalProfileKind,
  data: ProfileLike,
  overrides?: Partial<Pick<OperationalProfileEntry, 'isDraft' | 'ativo' | 'dirty'>>,
): OperationalProfileEntry {
  const id = data.id;
  return {
    localKey: newLocalKey(kind, id),
    kind,
    id,
    ativo: overrides?.ativo ?? data.ativo ?? false,
    isDraft: overrides?.isDraft ?? !id,
    dirty: overrides?.dirty,
    data: { ...data },
  };
}

export function sortOperationalProfiles(
  perfis: OperationalProfileEntry[],
): OperationalProfileEntry[] {
  return [...perfis].sort((a, b) => {
    if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
    if (a.isDraft !== b.isDraft) return a.isDraft ? -1 : 1;
    return (b.id ?? 0) - (a.id ?? 0);
  });
}

export function getOperationalProfileTabLabel(entry: OperationalProfileEntry): string {
  const tipo = OPERATIONAL_PROFILE_LABELS[entry.kind];
  return entry.id ? `${tipo} #${entry.id}` : tipo;
}

/** Sincroniza campos singulares do formulário de volta para a lista unificada. */
export function mergeSingularProfileFieldsIntoPerfis(input: PersonFormDto): void {
  const perfis = input.perfisOperacionais?.length
    ? [...input.perfisOperacionais]
    : buildPerfisOperacionaisFromForm(input);

  for (const kind of OPERATIONAL_PROFILE_KINDS) {
    const singular = input[kind];
    if (!singular) continue;

    const idx = perfis.findIndex((item) => {
      if (item.kind !== kind) return false;
      if (singular.id != null && item.id === singular.id) return true;
      return item.ativo || item.isDraft;
    });
    if (idx < 0) continue;

    const entry = perfis[idx];
    const mergedData = {
      ...singular,
      ativo: entry.dirty ? entry.ativo : (singular.ativo ?? entry.ativo),
    } as OperationalProfileData;

    const atualizados = updateOperationalProfileEntry(
      perfis,
      entry.localKey,
      mergedData,
    );
    perfis.splice(0, perfis.length, ...atualizados);
  }

  input.perfisOperacionais = sortOperationalProfiles(perfis);
}

export function buildPerfisOperacionaisFromForm(values: PersonFormDto): OperationalProfileEntry[] {
  if (values.perfisOperacionais?.length) {
    return sortOperationalProfiles(values.perfisOperacionais);
  }

  const entries: OperationalProfileEntry[] = [];

  const appendKind = (
    kind: OperationalProfileKind,
    current: ProfileLike | undefined | null,
    historico: ProfileLike[] | undefined,
  ) => {
    const historicoIds = new Set((historico ?? []).map((item) => item.id).filter(Boolean));

    if (current?.ativo) {
      entries.push(toEntry(kind, current, { ativo: true, isDraft: !current.id }));
    } else if (current?.id && !historicoIds.has(current.id)) {
      entries.push(toEntry(kind, current, { ativo: false, isDraft: false }));
    }

    for (const item of historico ?? []) {
      if (!item.id) continue;
      entries.push(toEntry(kind, item, { ativo: false, isDraft: false }));
    }
  };

  appendKind('defensor', values.defensor, values.defensoresHistorico);
  appendKind('estagiario', values.estagiario, values.estagiariosHistorico);
  appendKind('colaborador', values.colaborador, values.colaboradoresHistorico);

  return sortOperationalProfiles(entries);
}

export function getOperationalProfileDefaults(kind: OperationalProfileKind): OperationalProfileData {
  switch (kind) {
    case 'defensor':
      return {
        ativo: true,
        titulo: '',
        numero_oab: '',
        matricula_defensoria: '',
        anotacao: '',
        autorizacoesTrabalhoRemoto: [],
        vinculosAssessores: [],
      };
    case 'colaborador':
      return {
        ativo: true,
        tipo_vinculo: ColaboradorEnum.indefinido,
        vinculo_direto: false,
        cessao_onerosa: false,
        orgao_origem: '',
        anotacao: '',
        vinculosDefensores: [],
      };
    case 'estagiario':
      return {
        ativo: true,
        anotacao: '',
        estagios: [],
      };
    default:
      return { ativo: true };
  }
}

export function getOperationalProfileTableCells(entry: OperationalProfileEntry): {
  identificacao: string;
  inicio: string;
  termino: string;
} {
  const data = entry.data;
  switch (entry.kind) {
    case 'defensor': {
      const d = data as NonNullable<ReadPersonDto['defensor']>;
      return {
        identificacao: [d.numero_oab ? `OAB ${d.numero_oab}` : null, d.matricula_defensoria || null]
          .filter(Boolean)
          .join(' · ') || '—',
        inicio: formatDate(d.data_admissao),
        termino: formatDate(d.data_exoneracao),
      };
    }
    case 'colaborador': {
      const c = data as NonNullable<ReadPersonDto['colaborador']>;
      return {
        identificacao: [
          ColaboradorList.find((v) => v.value === c.tipo_vinculo)?.name,
          c.orgao_origem || null,
        ]
          .filter(Boolean)
          .join(' · ') || '—',
        inicio: formatDate(c.data_cessao),
        termino: formatDate(c.data_saida),
      };
    }
    case 'estagiario': {
      const e = data as NonNullable<ReadPersonDto['estagiario']>;
      const primeiro = e.estagios?.[0];
      return {
        identificacao: e.estagios?.length
          ? `${e.estagios.length} estágio(s)`
          : 'Sem estágios',
        inicio: formatDate(primeiro?.data_inicio_estagio),
        termino: formatDate(primeiro?.data_fim_estagio ?? primeiro?.desligamento),
      };
    }
    default:
      return { identificacao: '—', inicio: '—', termino: '—' };
  }
}

export function addOperationalProfileEntry(
  perfis: OperationalProfileEntry[],
  kind: OperationalProfileKind,
): OperationalProfileEntry[] {
  const defaults = getOperationalProfileDefaults(kind) as OperationalProfileData;
  const novo = toEntry(kind, defaults, { ativo: true, isDraft: true, dirty: true });

  const atualizados = perfis.map((item) => {
    if (item.isDraft) return null;
    if (item.ativo) {
      return {
        ...item,
        ativo: false,
        dirty: true,
        data: { ...item.data, ativo: false },
      };
    }
    return item;
  }).filter((item): item is OperationalProfileEntry => item != null);

  return sortOperationalProfiles([novo, ...atualizados]);
}

export function updateOperationalProfileEntry(
  perfis: OperationalProfileEntry[],
  localKey: string,
  data: OperationalProfileData,
): OperationalProfileEntry[] {
  return sortOperationalProfiles(
    perfis.map((item) =>
      item.localKey === localKey
        ? {
            ...item,
            dirty: true,
            id: data.id ?? item.id,
            isDraft: !data.id && item.isDraft,
            ativo: data.ativo ?? item.ativo,
            data: { ...data, ativo: data.ativo ?? item.ativo },
          }
        : item,
    ),
  );
}

export function removeOperationalProfileEntry(
  perfis: OperationalProfileEntry[],
  localKey: string,
): { perfis: OperationalProfileEntry[]; remocao?: OperationalProfileRemoval } {
  const alvo = perfis.find((item) => item.localKey === localKey);
  if (!alvo) return { perfis };

  const remocao = alvo.id ? { kind: alvo.kind, id: alvo.id } : undefined;
  return {
    perfis: perfis.filter((item) => item.localKey !== localKey),
    remocao,
  };
}

export function activateOperationalProfileEntry(
  perfis: OperationalProfileEntry[],
  localKey: string,
): OperationalProfileEntry[] {
  const alvo = perfis.find((item) => item.localKey === localKey);
  if (!alvo || alvo.isDraft || alvo.ativo) return perfis;

  return sortOperationalProfiles(
    perfis.map((item) => {
      if (item.localKey === localKey) {
        return {
          ...item,
          ativo: true,
          dirty: true,
          data: { ...item.data, ativo: true },
        };
      }
      if (item.ativo) {
        return {
          ...item,
          ativo: false,
          dirty: true,
          data: { ...item.data, ativo: false },
        };
      }
      return item;
    }),
  );
}

export function inactivateOperationalProfileEntry(
  perfis: OperationalProfileEntry[],
  localKey: string,
): OperationalProfileEntry[] {
  const alvo = perfis.find((item) => item.localKey === localKey);
  if (!alvo || alvo.isDraft || !alvo.ativo) return perfis;

  return sortOperationalProfiles(
    perfis.map((item) =>
      item.localKey === localKey
        ? {
            ...item,
            ativo: false,
            dirty: true,
            data: { ...item.data, ativo: false },
          }
        : item,
    ),
  );
}

export function resolveActiveOperationalProfileKind(
  values: PersonFormDto,
): OperationalProfileKind | null {
  const perfis = values.perfisOperacionais ?? buildPerfisOperacionaisFromForm(values);
  return perfis.find((item) => item.ativo)?.kind ?? null;
}

export function resolveAllowedAddKinds(
  pageTipo: 'todos' | 'defensor' | 'estagiario' | 'colaborador' | 'usuario',
  permissions: { defensor: boolean; estagiario: boolean; colaborador: boolean },
): OperationalProfileKind[] {
  const all: OperationalProfileKind[] = [];
  if (permissions.defensor && (pageTipo === 'todos' || pageTipo === 'defensor')) {
    all.push('defensor');
  }
  if (permissions.estagiario && (pageTipo === 'todos' || pageTipo === 'estagiario')) {
    all.push('estagiario');
  }
  if (permissions.colaborador && (pageTipo === 'todos' || pageTipo === 'colaborador')) {
    all.push('colaborador');
  }
  return all;
}

/** Converte a lista unificada para os campos singulares esperados pela API. */
export function syncPerfisOperacionaisToPayload(input: PersonFormDto): void {
  mergeSingularProfileFieldsIntoPerfis(input);

  const perfis = input.perfisOperacionais?.length
    ? input.perfisOperacionais
    : buildPerfisOperacionaisFromForm(input);

  const removidos = [...(input.perfisOperacionaisRemovidos ?? [])];

  delete input.perfisOperacionais;
  delete input.defensoresHistorico;
  delete input.estagiariosHistorico;
  delete input.colaboradoresHistorico;
  delete input.defensor;
  delete input.estagiario;
  delete input.colaborador;
  delete input.perfisOperacionaisRemovidos;

  if (removidos.length) {
    (input as PersonFormDto & { perfisOperacionaisExcluir?: OperationalProfileRemoval[] }).perfisOperacionaisExcluir =
      removidos;
  }

  for (const kind of OPERATIONAL_PROFILE_KINDS) {
    const doTipo = perfis.filter((item) => item.kind === kind);
    const ativo = doTipo.find((item) => item.ativo);
    const rascunho = doTipo.find((item) => item.isDraft);
    const reativado = doTipo.find((item) => item.dirty && item.ativo && item.id);
    const inativado = doTipo.find(
      (item) => item.dirty && !item.ativo && item.id && !item.isDraft,
    );

    const enviar = ativo ?? rascunho ?? reativado ?? inativado;
    if (enviar) {
      input[kind] = {
        ...enviar.data,
        ativo: enviar.ativo,
        ...(enviar.id ? { id: enviar.id } : {}),
      };
    }
  }
}

export function prepareOperationalProfileForEdit(
  entry: OperationalProfileEntry,
): OperationalProfileData {
  return structuredClone(entry.data);
}
